'use strict';

// external imports
import omniclone from 'omniclone';
import {keys} from 'ts-transformer-keys';

// shared imports
import metaModel from '~/shared/src/utilities/metaModel';
import ReferencePropertySet from '~/shared/src/mm/ReferencePropertySet';
import Reference from '~/shared/src/ci/Reference';
import ReferenceProperty, {
    ReferencePropertyAttributes,
    ReferencePropertyAttributesForCreation,
    ReferencePropertyAttributesForUpdate,
    ReferencePropertyAttributesSettable,
    ReferencePropertyJsonAttributes,
    isReferencePropertyAttributes,
    isReferencePropertyJsonAttributes
} from '~/shared/src/ci/ReferenceProperty';
import LocalizableError from '~/shared/src/base/LocalizableError';
import {
    ReferenceMetaDataMapping,
    ReferencePropertyMetaDataMapping
} from '~/shared/src/utilities/modules/data';


// type definitions
export type EnumReferencePropertyAttributes = {
    referenceId : number;
    referencePropertySetId : number;
    properties? : ( ReferencePropertyAttributes & Required<Pick<ReferencePropertyAttributes, 'key'>> )[];
};
export const isEnumReferencePropertyAttributes = ( enumReferencePropoertyAttributesToTest : any ) : enumReferencePropoertyAttributesToTest is EnumReferencePropertyAttributes => {
    let retVal = false;

    try {
        if ( typeof enumReferencePropoertyAttributesToTest !== 'object' ) {
            throw new Error();
        }

        if ( enumReferencePropoertyAttributesToTest.referenceId === undefined || typeof enumReferencePropoertyAttributesToTest.referenceId !== 'number' ) {
            throw new Error();
        }
        if ( enumReferencePropoertyAttributesToTest.referencePropertySetId === undefined || typeof enumReferencePropoertyAttributesToTest.referencePropertySetId !== 'number' ) {
            throw new Error();
        }

        if ( enumReferencePropoertyAttributesToTest.properties !== undefined ) {
            if ( !Array.isArray( enumReferencePropoertyAttributesToTest.properties as any ) ) {
                throw new Error();
            }

            for ( const propertyAttributes of enumReferencePropoertyAttributesToTest.properties ) {
                if ( !isReferencePropertyAttributes( { referenceId: 1, ...propertyAttributes } as any ) ) {
                    throw new Error();
                }
            }
        }

        retVal = true;
    }
    catch ( e ) {
        /*empty*/
    }

    return retVal;
};

export type EnumReferencePropertyJsonAttributes = Omit<EnumReferencePropertyAttributes, 'properties'>
    & { properties? : ( ReferencePropertyJsonAttributes & Required<Pick<ReferencePropertyJsonAttributes, 'key'>> )[] };
export const isEnumReferencePropertyJsonAttributes = ( enumReferencePropertyJsonAttributesToTest : any ) : enumReferencePropertyJsonAttributesToTest is EnumReferencePropertyJsonAttributes => {
    let retVal = false;

    try {
        if ( typeof enumReferencePropertyJsonAttributesToTest !== 'object' ) {
            throw new Error();
        }

        if ( enumReferencePropertyJsonAttributesToTest.referenceId === undefined || typeof enumReferencePropertyJsonAttributesToTest.referenceId !== 'number' ) {
            throw new Error();
        }
        if ( enumReferencePropertyJsonAttributesToTest.referencePropertySetId === undefined || typeof enumReferencePropertyJsonAttributesToTest.referencePropertySetId !== 'number' ) {
            throw new Error();
        }

        if ( enumReferencePropertyJsonAttributesToTest.properties !== undefined ) {
            if ( !Array.isArray( enumReferencePropertyJsonAttributesToTest.properties as any ) ) {
                throw new Error();
            }

            for ( const propertyJsonAttributes of enumReferencePropertyJsonAttributesToTest.properties ) {
                if ( !isReferencePropertyJsonAttributes( propertyJsonAttributes ) ) {
                    throw new Error();
                }
            }
        }

        retVal = true;
    }
    catch ( e ) {
        /*empty*/
    }

    return retVal;
};

export type EnumReferencePropertyOnlyAttributesSettable = Pick<ReferencePropertyAttributes, never>;
export const enumReferencePropertyOnlyAttributesSettable = keys<EnumReferencePropertyOnlyAttributesSettable>() as string[];
export type EnumReferencePropertyAttributesSettable = EnumReferencePropertyOnlyAttributesSettable
    & { properties? : { [ key : string ] : ReferencePropertyAttributesSettable } };
export const enumReferencePropertyAttributesSettable = keys<EnumReferencePropertyAttributesSettable>() as string[];

export type EnumReferencePropertyOnlyAttributesForCreation = Required<Pick<EnumReferencePropertyAttributes, 'referenceId' | 'referencePropertySetId'>>;
export type EnumReferencePropertyAttributesForCreation = EnumReferencePropertyOnlyAttributesForCreation
    & { properties? : ReferencePropertyAttributesForCreation[] };

export type EnumReferencePropertyOnlyAttributesForUpdate = Pick<ReferencePropertyAttributes, never>;
export type EnumReferencePropertyAttributesForUpdate = EnumReferencePropertyOnlyAttributesForUpdate
    & { properties? : ( ReferencePropertyAttributesForUpdate | ReferencePropertyAttributesForCreation )[] };


/**
 * The EnumReferenceProperty class.
 */
class EnumReferenceProperty {


    /*** ATTRIBUTES ********************************************************************************************************************/

    /**
     * The ReferenceId the EnumReferenceProperty belongs to.
     */
    protected _referenceId : number;
    get referenceId() {
        return this._referenceId;
    }

    /**
     * The ReferencePropertySetId of the EnumReferenceProperty.
     */
    protected _referencePropertySetId : number;
    get referencePropertySetId() {
        return this._referencePropertySetId;
    }


    /*** ALIASES ***********************************************************************************************************************/

    /**
     * The ReferencePropertySet of this ReferenceProperty.
     */
    get referencePropertySet() : ReferencePropertySet {
        return metaModel[ this._schema ].getReferencePropertySet( this._referencePropertySetId )!;
    }


    /*************************************************************************************************************************************/

    /**
     * The Reference this EnumReferenceProperty belongs to
     */
    protected _parent : Reference;
    get parent() {
        return this._parent;
    }

    set parent( parent : Reference ) {
        if ( this._parent instanceof Reference && parent instanceof Reference ) {
            this._parent = parent;
        }
    }

    /**
     * The database schema this EnumReferenceProperty belongs to
     */
    protected _schema : Schema;
    get schema() {
        return this._schema;
    }

    /**
     * An map containing the ReferenceProperties by ReferencePropertyKey.
     */
    protected _properties : Map<string, ReferenceProperty>;

    get properties() {
        return this._properties;
    }

    protected _isReorderingProperties : boolean = false;
    get isReorderingProperties() {
        return this._isReorderingProperties;
    }

    /**
     *The constructor.
     *
     * @param parent                                 The parent Reference this belongs to
     * @param attributes                             An object containing attribute-value pairs for this
     *
     */
    constructor( parent : Reference, attributes : EnumReferencePropertyAttributes ) {

        if ( attributes.referenceId === 0 ) {
            throw new Error( 'YN.base.dataObject.referenceProperty.attributes.validation.referenceId.isEmpty' );
        }

        if ( attributes.referencePropertySetId === 0 ) {
            throw new Error( 'YN.base.dataObject.referenceProperty.attributes.validation.referencePropertySetId.isEmpty' );
        }

        if ( attributes.referencePropertySetId !== metaModel[ parent.schema ].getReferencePropertySet( attributes.referencePropertySetId )?.id ) {
            throw new LocalizableError(
                'YN.base.dataObject.referenceProperty.attributes.validation.referencePropertySetId.invalid',
                { referencePropertySetId: String( attributes.referencePropertySetId ), schema: parent.schema }
            );
        }

        if ( !metaModel[ parent.schema ].getReferencePropertySet( attributes.referencePropertySetId )?.isEnumerated ) {
            throw new LocalizableError( 'YN.base.dataObject.referenceProperty.attributes.validation.referencePropertySetId.notEnumerated' )
        }

        this._parent = parent;

        this._schema = parent.schema;

        this._referenceId = attributes.referenceId;
        this._referencePropertySetId = attributes.referencePropertySetId;

        this._properties = new Map();
        if ( attributes.properties ) {
            this.addPropertiesFromJSON( attributes.properties );
        }
    }

    /**
     * Returns an object containing the attributes with their values limited by includeAttributes parameter.
     *
     * @param [includeAttributes=all] The state of the attributes to include in result.
     */
    toJSON( includeAttributes : 'all' | 'modified' = 'all' ) : EnumReferencePropertyJsonAttributes {
        // required attributes
        const retVal : EnumReferencePropertyJsonAttributes = {
            referenceId           : this._referenceId,
            referencePropertySetId: this._referencePropertySetId
        };

        if ( this._properties.size ) {
            const properties : EnumReferencePropertyJsonAttributes[ 'properties' ] = [];

            for ( const property of this._properties.values() ) {
                if ( property.isNew() || property.isModified() || includeAttributes !== 'modified' ) {
                    properties.push( property.toJSON( includeAttributes ) as Required<EnumReferencePropertyJsonAttributes>[ 'properties' ][ number ] );
                }
            }

            if ( properties.length ) {
                retVal.properties = properties;
            }
        }

        return retVal;
    }

    /**
     * Returns an object containing the attributes with their values limited by includeAttributes parameter.
     *
     * @param [includeAttributes=all] The state of the attributes to include in result.
     */
    toUpdateJSON( includeAttributes : 'all' | 'modified' = 'all' ) : EnumReferencePropertyAttributesSettable {
        // required attributes
        const retVal : EnumReferencePropertyAttributesSettable = {};

        if ( this._properties.size ) {
            const properties : EnumReferencePropertyAttributesSettable[ 'properties' ] = {};

            for ( const [ key, referenceProperty ] of this._properties.entries() ) {
                if ( referenceProperty.isNew() || referenceProperty.isModified() || includeAttributes !== 'modified' ) {
                    properties[ key ] = referenceProperty.toUpdateJSON( includeAttributes );
                }
            }

            if ( Object.keys( properties ).length ) {
                retVal.properties = properties;
            }
        }

        return retVal;
    }

    /**
     * Builds and returns the signature string for this
     */
    toString() : string {
        return `EnumReferenceProperty '${this.referencePropertySet.name}' of ${this._parent}`;
    }

    /**
     * Returns a string containing the XML representation of this
     * @param {boolean} [excludeDeleted]
     */
    toXML( excludeDeleted : boolean = false, indent : number = 0 ) : string {
        const properties : ReferenceProperty[] = [ ...this._properties.values() ];

        let retVal = '';

        properties.sort( ( property1, property2 ) => {
            if ( property1.key && property2.key ) {
                if ( property1.key > property2.key ) {
                    return 1;
                }
                else if ( property1.key < property2.key ) {
                    return -1;
                }
            }

            return 0;
        } );

        for ( const property of properties ) {
            if ( !excludeDeleted || !property.isDeleted() ) {
                if ( retVal ) {
                    retVal += '\r\n';
                }
                retVal += property.toXML( excludeDeleted, indent );
            }
        }

        return retVal;
    }

    /**
     * Resets all settable attributes to their initial values.
     */
    reset() : EnumReferenceProperty {
        // reset all properties
        for ( const property of this._properties.values() ) {
            // remove new property
            if ( property.isNew() ) {
                this._properties.delete( property.stringKey );
            }
            // reset existing property
            else {
                property.reset();
            }
        }

        return this;
    }

    /**
     * Commits all changes of settable attributes.
     */
    commit( referenceMetaDataMapping? : ReferenceMetaDataMapping, referencePropertyMetaDataMapping? : ReferencePropertyMetaDataMapping ) : EnumReferenceProperty {

        // update itemId if new and mapped one is given
        if ( this.isNew() && referenceMetaDataMapping?.[ this._referenceId ]?.id ) {
            this._referenceId = referenceMetaDataMapping[ this._referenceId ].id;
        }

        //  commit all Properties
        for ( const property of this._properties.values() ) {
            property.commit( referencePropertyMetaDataMapping );
        }

        return this;
    }

    /**
     * Apply given attributes to this enum property.
     *
     * @param [attributes]          An object containing attribute-value pairs to be applied to the clone
     * @param [clone]               true/false to use clone/update functions of properties
     */
    update( attributes : EnumReferencePropertyAttributesSettable = {}, clone : boolean = false ) : EnumReferenceProperty {
        const deletedStatusIds = metaModel[ this._schema ].getDeletedStatus().map( s => s.id );

        // handle properties
        if ( attributes.properties ) {
            for ( const stringKey of Object.keys( attributes.properties ) as string[] ) {
                const propertyAttributes = attributes.properties[ stringKey ];

                let property = this._properties.get( stringKey );

                // clone existing property first if
                if ( clone && property && !property.isNew() ) {
                    property = property.clone();

                    // update property in map
                    this._properties.set( stringKey, property );
                }

                // add property
                if ( !property && ( propertyAttributes.statusId === undefined || !deletedStatusIds.includes( propertyAttributes[ 'statusId' ] ) ) ) {
                    this.addPropertyFromJSON( {
                        referenceId           : this._referenceId,
                        referencePropertySetId: this._referencePropertySetId,
                        ...propertyAttributes
                    } as EnumReferencePropertyAttributes );
                }

                // delete property
                else if (
                    property
                    && !property.isDeleted()
                    && propertyAttributes.statusId !== undefined
                    && deletedStatusIds.includes( propertyAttributes[ 'statusId' ] )
                ) {
                    // remove new property
                    if ( property.isNew() ) {
                        this._properties.delete( stringKey );

                        this.reorderProperties();
                    }

                    // delete existing property
                    else {
                        property.reset().update( { statusId: propertyAttributes.statusId } );
                    }
                }

                // normal property update
                else if ( property ) {
                    property = property.update( propertyAttributes );
                }
            }
        }

        return this;
    }

    /**
     * Creates and returns a clone of this with given attributes applied.
     *
     * @param [attributes]          An object containing attribute-value pairs to be applied to the clone
     */
    clone( attributes : EnumReferencePropertyAttributesSettable = {} ) : EnumReferenceProperty {
        const clone : EnumReferenceProperty = omniclone(
            this,
            {
                invokeConstructors: false,
                setPrototype      : true,
                copyGettersSetters: true
            },
            ( node : any ) => {
                // do not clone parent and properties
                if ( node instanceof Reference || node instanceof ReferenceProperty ) {
                    return node;
                }
                else {
                    return undefined;
                }
            }
        );

        // clone and update parent in properties
        for ( const stringKey of clone.properties.keys() ) {
            const property = clone.properties.get( stringKey )!.clone();

            property.parent = clone;

            clone.properties.set( stringKey, property );
        }

        clone.update( attributes, true );

        return clone;
    }

    /**
     * Checks if this is new.
     */
    isNew() : boolean {
        let retVal = true;

        // check if one property is not new
        for ( const property of this._properties.values() ) {
            if ( !property.isNew() ) {
                retVal = false;
                break;
            }
        }

        return retVal;
    }

    /**
     * Checks if a Property of this is modified.
     */
    isModified() : boolean {
        let retVal = false;

        // check if one property is modified
        for ( const property of this._properties.values() ) {
            if ( property.isModified() || property.isNew() ) {
                retVal = true;
                break;
            }
        }

        return retVal;
    }

    /**
     * Checks if all Properties of this are valid.
     */
    isValid() : boolean {
        let retVal = true;

        // check if one property is not valid
        for ( const property of this._properties.values() ) {
            if ( !property.isValid() ) {
                retVal = false;
                break;
            }
        }

        return retVal;
    }

    isKeyUnique( key : AttributeValue, referenceProperty? : ReferenceProperty ) : boolean {
        let retVal : boolean = true;

        // check if key is unique within properties
        for ( const otherProperty of this._properties.values() ) {
            if (
                !otherProperty.isDeleted()
                && !( otherProperty === referenceProperty || otherProperty.id === referenceProperty?.id )
                && !( referenceProperty && referenceProperty.orderNum !== otherProperty.orderNum )
                && otherProperty.key === key
            ) {
                retVal = false;
                break;
            }
        }

        return retVal;
    }

    isOrderNumUnique( orderNum : number, referenceProperty? : ReferenceProperty ) : boolean {
        let retVal : boolean = true;

        // check if ordeNum is unique within properties
        for ( const otherProperty of this._properties.values() ) {
            if (
                !otherProperty.isDeleted()
                && !( otherProperty === referenceProperty || otherProperty.id === referenceProperty?.id )
                && otherProperty.orderNum === orderNum
            ) {
                retVal = false;
                break;
            }
        }

        return retVal;
    }

    getUniqueKeys() : AttributeValue[] {
        const retVal : AttributeValue[] = [];

        for ( const property of this._properties.values() ) {
            const key = property.key;

            if ( !property.isDeleted() && !retVal.includes( key ) ) {
                retVal.push( key );
            }
        }

        return retVal;
    }

    getUniqueValues() : AttributeValue[] {
        const retVal : AttributeValue[] = [];

        for ( const property of this._properties.values() ) {
            const value = property.value;

            if ( !property.isDeleted() && !retVal.includes( value ) ) {
                retVal.push( value );
            }
        }

        return retVal;
    }

    getMaxOrderNum() : number {
        let retVal : number = 0;

        // get max orderNum from properties
        for ( const property of this._properties.values() ) {
            if ( !property.isDeleted() && property.orderNum > retVal ) {
                retVal = property.orderNum;
            }
        }

        return retVal;
    }

    /*** PROPERTIES RELATED **************************************************************************************************************/

    /**
     * Adds Properties given by data to this.
     *
     * @param dataObject            An array of referencePropertyData
     */
    addPropertiesFromJSON( dataObject : Partial<ReferencePropertyAttributes>[] ) {
        for ( const referencePropertyData of dataObject ) {
            this.addPropertyFromJSON( referencePropertyData );
        }
    }

    /**
     * Adds a Property given by data to this.
     *
     * @param dataObject            An object containing attribute-value pairs
     */
    addPropertyFromJSON( dataObject : Partial<ReferencePropertyAttributes> ) : ReferenceProperty {
        const referencePropertySet = this.referencePropertySet;

        let newKey : string,
            newValue : AttributeValue | undefined = dataObject.value;

        // always convert to string
        if (
            typeof dataObject.key !== 'string'
            || (
                referencePropertySet.isKeyOrdered
                && referencePropertySet.keyFromStringRegExp
                && !referencePropertySet.keyFromStringRegExp.exec( dataObject.key )
            )
        ) {
            newKey = referencePropertySet.getStringKey( { key: dataObject.key ?? '', orderNum: this.getMaxOrderNum() + 1 } );
        }
        else {
            newKey = dataObject.key;
        }

        if ( this._properties.has( newKey ) ) {
            throw new LocalizableError(
                'YN.base.dataObject.enumReferenceProperty.addProperty.exists',
                { key: newKey, propertyTypeName: this.referencePropertySet.name }
            );
        }

        // convert only to string if ordered by value
        if (
            referencePropertySet.isValueOrdered
            && (
                typeof dataObject.value !== 'string'
                || (
                    referencePropertySet.valueFromStringRegExp
                    && !referencePropertySet.valueFromStringRegExp.exec( dataObject.value )
                )
            )
        ) {
            newValue = referencePropertySet.getStringValue( { value: dataObject.value ?? '', orderNum: this.getMaxOrderNum() + 1 } );
        }

        const newReferenceProperty = new ReferenceProperty( this, { ...dataObject, referencePropertySetId: this._referencePropertySetId, referenceId: this._referenceId, key: newKey, value: newValue } );

        this._properties.set( newReferenceProperty.stringKey, newReferenceProperty );

        return newReferenceProperty;
    }

    /**
     * Adds a new Property by key to this.
     *
     * @param key
     */
    addProperty( key : AttributeValue ) : ReferenceProperty {
        const referencePropertySet = this.referencePropertySet,
              newKey : string      = referencePropertySet.getStringKey( { key: key, orderNum: this.getMaxOrderNum() + 1 } );

        if ( this._properties.has( newKey ) ) {
            throw new LocalizableError(
                'YN.base.dataObject.enumReferenceProperty.addProperty.exists',
                { key: newKey, propertyTypeName: this.referencePropertySet.name }
            );
        }

        const newReferenceProperty = new ReferenceProperty( this, { referenceId: this._referenceId, referencePropertySetId: this._referencePropertySetId, key: newKey } );

        this._properties.set( newKey, newReferenceProperty );

        return newReferenceProperty;
    }

    updatePropertyKey( property : ReferenceProperty, newStringKey? : string ) {
        if ( !newStringKey ) {
            newStringKey = property.stringKey;
        }

        for ( const [ oldStringKey, propertyToUpdate ] of this._properties ) {
            if ( propertyToUpdate === property ) {
                if ( oldStringKey !== newStringKey ) {
                    this._properties.delete( oldStringKey );
                    this._properties.set( newStringKey, property );
                }
                break;
            }
        }
    }

    reorderProperties( property? : ReferenceProperty, orderNum? : number, clone : boolean = false ) {
        this._isReorderingProperties = true;

        const referencePropertySet                                            = this.referencePropertySet,
              propertiesToOrder : { stringKey : string; orderNum : number }[] = [];

        if ( !this.parent.isDeleted() && ( referencePropertySet.isKeyOrdered || referencePropertySet.isValueOrdered ) ) {

            // get all non-deleted properties without property to update
            for ( const [ stringKey, propertyToOrder ] of this._properties.entries() ) {
                if ( !propertyToOrder.isDeleted() && propertyToOrder !== property ) {
                    propertiesToOrder.push( {
                        stringKey,
                        orderNum: propertyToOrder.orderNum
                    } );
                }
            }

            // sort by orderNum
            propertiesToOrder.sort( ( p1, p2 ) => {
                if ( p1.orderNum > p2.orderNum ) {
                    return 1;
                }
                else if ( p1.orderNum < p2.orderNum ) {
                    return -1;
                }
                else {
                    return 0;
                }
            } );

            // insert property to update at new orderNum
            if ( property && orderNum ) {
                propertiesToOrder.splice( orderNum - 1, 0, { stringKey: property.stringKey, orderNum: orderNum } );
            }

            // move away if orderNum not the same as in array and not unique
            for ( let i = 0; i < propertiesToOrder.length; i++ ) {
                const newOrderNum = i + 1;

                let propertyToOrder = this._properties.get( propertiesToOrder[ i ].stringKey );

                if ( propertyToOrder && propertyToOrder.orderNum !== newOrderNum ) {
                    if ( clone && propertyToOrder !== property ) {
                        propertyToOrder = propertyToOrder.clone();

                        // update property in map
                        this._properties.set( propertiesToOrder[ i ].stringKey, propertyToOrder );
                    }

                    propertyToOrder.update( { orderNum: this.getMaxOrderNum() + 1 } );

                    propertiesToOrder[ i ].stringKey = propertyToOrder.stringKey;
                }
            }

            // update orderNum if not the same as in array
            for ( let i = 0; i < propertiesToOrder.length; i++ ) {
                const newOrderNum = i + 1;

                let propertyToOrder = this._properties.get( propertiesToOrder[ i ].stringKey );

                if ( propertyToOrder && propertyToOrder.orderNum !== newOrderNum ) {
                    if ( clone && propertyToOrder !== property ) {
                        propertyToOrder = propertyToOrder.clone();

                        // update property in map
                        this._properties.set( propertiesToOrder[ i ].stringKey, propertyToOrder );
                    }

                    propertyToOrder.update( { orderNum: newOrderNum } );
                }
            }
        }

        this._isReorderingProperties = false;
    }
}

export default EnumReferenceProperty;
